import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import DialogGameCreate from "./dialogGameCreate";
import { adminScreenFrame, DANGER } from "./settingsPalette";
import { resolveLogoUrl } from "../../modules/accountLogos";
import { deleteGame, findRuntimeGames } from "../../modules/adminApi";

const useStyles = makeStyles(() => ({
  ...adminScreenFrame,
  rowText: { flex: 1, minWidth: 0 },
  logo: { width: "72px", height: "72px", objectFit: "contain", marginBottom: "0.75rem" },
  smallLogo: { width: "32px", height: "32px", objectFit: "contain", marginRight: "10px" },
  gameRow: { display: "flex", alignItems: "center" },
  dangerBlock: { marginTop: "1rem", padding: "0.75rem 1rem", border: `1px solid ${DANGER}`, borderRadius: "4px" },
  dangerTitle: { fontSize: "0.72rem", letterSpacing: 0.5, textTransform: "uppercase", color: DANGER },
  dangerActions: { display: "flex", gap: "8px", marginTop: "0.5rem" },
  dangerButton: { color: DANGER, borderColor: DANGER },
}));

export default function RuntimeGamesPanel({ canDelete, createSnackbar, headerAction, onShowBases }) {
  const classes = useStyles();
  const [games, setGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedID, setSelectedID] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const selectedGame = games.find((game) => game._id === selectedID) || null;

  const reload = async () => {
    const result = await findRuntimeGames();
    if (result.ok) setGames(result.data || []);
    else createSnackbar("Could not load the runtime games.", "error", false);
    setIsLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const runDelete = async () => {
    if (!selectedGame) return;
    setIsSaving(true);
    const result = await deleteGame(selectedGame._id);
    setIsSaving(false);
    if (!result.ok) {
      createSnackbar(
        result.data === "GAME_HAS_BASES" ? "Delete this game's bases before deleting the game." : `Could not delete the game: ${result.data || result.status}`,
        "error", false
      );
      return;
    }
    createSnackbar(`${selectedGame.title} deleted.`, "success", false);
    setSelectedID(null);
    setConfirming(false);
    setConfirmTitle("");
    await reload();
  };

  if (isLoading) return <div className={classes.root}><div className={classes.centred}><CircularProgress /></div></div>;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography className={classes.title}>Runtime games</Typography>
        <div className={classes.headerSpacer} />
        {headerAction}
        <Button variant="outlined" onClick={onShowBases}>Runtime bases</Button>
        <Button variant="contained" color="primary" onClick={() => setIsDialogOpen(true)}>+ New game</Button>
      </div>

      <DialogGameCreate
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCreated={async (game) => { setIsDialogOpen(false); await reload(); setSelectedID(game.gameID); }}
        createSnackbar={createSnackbar}
      />

      <div className={classes.body}>
        <div className={classes.listColumn}>
          <div className={classes.listHeader}>
            <div className={`${classes.listLabel} ${classes.listLabelSpaced}`}>Runtime games ({games.length})</div>
          </div>
          <div className={classes.listScroller}>
            {games.length === 0 && <div className={classes.detailEmpty}>No runtime games yet.</div>}
            {games.map((game) => (
              <div
                key={game._id}
                className={`${classes.listRow} ${game._id === selectedID ? classes.listRowSelected : ""}`}
                onClick={() => { setSelectedID(game._id); setConfirming(false); setConfirmTitle(""); }}
              >
                <div className={classes.gameRow}>
                  {game.image && <img className={classes.smallLogo} src={resolveLogoUrl(game.image)} alt="" />}
                  <div className={classes.rowText}><div className={classes.rowTitle}>{game.title}</div></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={classes.detailColumn}>
          {!selectedGame && <div className={classes.detailEmpty}>Select a game.</div>}
          {selectedGame && (
            <>
              <Typography className={classes.detailTitle}>{selectedGame.title}</Typography>
              <div className={classes.detailRule} />
              <div className={classes.tabBody}>
                {selectedGame.image && <img className={classes.logo} src={resolveLogoUrl(selectedGame.image)} alt={`${selectedGame.title} logo`} />}
                <Typography className={classes.hint}>id {selectedGame._id}</Typography>
                <Typography className={classes.hint}>Add a runtime base under this game, then grant the base to companies.</Typography>
                {canDelete && (
                  <div className={classes.dangerBlock}>
                    <Typography className={classes.dangerTitle}>Delete</Typography>
                    <Typography className={classes.hint}>Delete the game after all of its bases have been removed.</Typography>
                    {!confirming ? (
                      <Button variant="outlined" className={classes.dangerButton} onClick={() => setConfirming(true)}>Delete game</Button>
                    ) : (
                      <>
                        <Typography className={classes.hint}>Type the game title to confirm deletion.</Typography>
                        <TextField className={classes.field} size="small" placeholder={selectedGame.title} value={confirmTitle} onChange={(event) => setConfirmTitle(event.target.value)} />
                        <div className={classes.dangerActions}>
                          <Button variant="contained" className={classes.dangerButton} disabled={isSaving || confirmTitle.trim() !== selectedGame.title} onClick={runDelete}>Delete game</Button>
                          <Button variant="outlined" disabled={isSaving} onClick={() => { setConfirming(false); setConfirmTitle(""); }}>Cancel</Button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
